import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { BaseProductSet, ProductCart, ProductSet, ProductSetInventory, ProductSetOrder } from 'shared';

const initialState: FurnitureAndEquipmentState = {
  productSets: null,
  baseProductSets: null,
  productCart: null,
  productSetOrders: null,
  productSetInventory: null,
  isCartToastVisible: false,
  cartToastTimeoutId: null
};

export type FurnitureAndEquipmentState = {
  productSets: ProductSet[] | null;
  baseProductSets: BaseProductSet[] | null;
  productCart: ProductCart | null;
  productSetOrders: ProductSetOrder[] | null;
  productSetInventory: ProductSetInventory[] | null;
  isCartToastVisible: boolean;
  cartToastTimeoutId: NodeJS.Timeout | null;
};

const furnitureAndEquipmentSlice = createSlice({
  name: 'FurnitureAndEquipment',
  initialState,
  reducers: {
    setProductSets: (state, action: PayloadAction<ProductSet[]>) => {
      state.productSets = action.payload;
    },
    setBaseProductSets: (state, action: PayloadAction<BaseProductSet[]>) => {
      state.baseProductSets = action.payload;
    },
    setProductCart: (state, action: PayloadAction<ProductCart>) => {
      state.productCart = action.payload;
    },
    setProductSetOrders: (state, action: PayloadAction<ProductSetOrder[]>) => {
      state.productSetOrders = action.payload;
    },
    addProductSetOrder: (state, action: PayloadAction<ProductSetOrder>) => {
      if (!state.productSetOrders) return;

      state.productSetOrders.unshift(action.payload);
    },
    setProductSetInventory: (state, action: PayloadAction<ProductSetInventory[]>) => {
      state.productSetInventory = action.payload;
    },
    addProductSetInventoryItems: (state, action: PayloadAction<ProductSetInventory[]>) => {
      if (!state.productSetInventory) return;

      const currentItems: ProductSetInventory[] = Array.isArray(state.productSetInventory)
        ? state.productSetInventory
        : (Object.values(state.productSetInventory) as ProductSetInventory[]);
      const statusKey = (item: ProductSetInventory) => `${item.product_set.id}|${item.status ?? 'completed'}`;
      const byKey = new Map(currentItems.map(item => [statusKey(item), { ...item }]));

      action.payload.forEach(item => {
        const key = statusKey(item);
        const existing = byKey.get(key);
        if (existing) {
          existing.rental_quantity += item.rental_quantity;
          existing.purchase_quantity += item.purchase_quantity;
        } else {
          byKey.set(key, {
            product_set: item.product_set,
            rental_quantity: item.rental_quantity,
            purchase_quantity: item.purchase_quantity,
            status: item.status ?? 'completed'
          });
        }
      });

      state.productSetInventory = Array.from(byKey.values());
    },
    setHasGivenFeedback: (state, action: PayloadAction<{ id: number; product_set_order_id: number }>) => {
      if (!state.productSetOrders) return;
      const order = state.productSetOrders.find(order => order.id === action.payload.product_set_order_id);
      if (!order) return;

      const group = order.product_set_rental_groups.find(group => group.id === action.payload.id);
      if (!group) return;

      group.has_given_feedback = true;
    },
    setHasGivenFurniturePurchaseInquiry: (state, action: PayloadAction<{ id: number }>) => {
      if (!state.productSets) return;

      const productSet = state.productSets.find(set => set.id === action.payload.id);
      if (!productSet) return;

      productSet.has_given_furniture_purchase_inquiry = true;
    },
    setCartToastVisible: (state, action: PayloadAction<boolean>) => {
      state.isCartToastVisible = action.payload;
    },
    setCartToastTimeoutId: (state, action: PayloadAction<NodeJS.Timeout | null>) => {
      state.cartToastTimeoutId = action.payload;
    }
  }
});

export const { actions } = furnitureAndEquipmentSlice;
export default furnitureAndEquipmentSlice.reducer;
